import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Mail, Lock, Eye, EyeOff, Users, TrendingUp, PieChart, UsersRound } from "lucide-react";

function SaleHubMark({ className }) {
    return (
        <svg viewBox="0 0 64 64" className={className} xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
                <linearGradient id="sh-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#60a5fa" />
                    <stop offset="1" stopColor="#2563eb" />
                </linearGradient>
            </defs>
            <path d="M32 2 L55 15.5 L55 48.5 L32 62 L9 48.5 L9 15.5 Z" fill="url(#sh-grad)" />
            <path d="M32 2 L9 15.5 L9 48.5 L32 62 Z" fill="#1e40af" opacity="0.25" />
            <text x="32" y="34" textAnchor="middle" dominantBaseline="central" fontSize="34" fontWeight="800" fill="#ffffff" fontFamily="Arial, Helvetica, sans-serif">S</text>
        </svg>
    );
}

function Feature({ icon: Icon, label }) {
    return (
        <div className="flex flex-col items-center gap-1.5">
            <Icon className="w-5 h-5 text-blue-400" />
            <span className="text-[11px] leading-tight text-slate-200">{label}</span>
        </div>
    );
}

export default function Login() {
    const { user, login, error } = useAuth();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [showPw, setShowPw] = useState(false);

    if (user && user !== false) {
        const dest = location.state?.from?.pathname || "/dashboard";
        return <Navigate to={dest} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        await login(email.trim(), password);
        setBusy(false);
    };

    const field = "w-full h-12 pl-11 pr-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 placeholder:text-slate-400 outline-none transition focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10";

    return (
        <div className="min-h-screen w-full bg-slate-100 grid place-items-center p-4 sm:p-6" data-testid="login-page">
            <div className="w-full max-w-5xl grid lg:grid-cols-2 bg-white rounded-3xl shadow-2xl ring-1 ring-slate-200/70 overflow-hidden min-h-[620px]">
                {/* Left brand panel */}
                <div className="relative hidden lg:flex flex-col justify-between p-10 text-white bg-[#0b1b3a] overflow-hidden">
                    <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2000&auto=format&fit=crop)" }} />
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0b1b3a] via-[#0b1b3a]/92 to-[#153a7e]/85" />
                    <div className="relative z-10 flex items-center gap-3">
                        <SaleHubMark className="w-11 h-11" />
                        <div>
                            <div className="text-2xl font-bold leading-none"><span className="text-white">Sale</span> <span className="text-blue-400">Hub</span></div>
                            <div className="text-xs text-slate-300 mt-1.5">One Hub. All Your Sales.</div>
                        </div>
                    </div>
                    <div className="relative z-10 max-w-md">
                        <h1 className="text-4xl font-bold leading-tight tracking-tight">
                            Empowering your team.<br />
                            <span className="text-blue-400">Driving your success.</span>
                        </h1>
                        <p className="text-slate-300 mt-5 leading-relaxed">
                            Sale Hub is your all-in-one platform to manage leads, customers, quotations, and sales performance — anytime, anywhere.
                        </p>
                        <div className="h-1 w-16 bg-blue-500 rounded-full mt-6" />
                    </div>
                    <div className="relative z-10 grid grid-cols-4 gap-3 text-center">
                        <Feature icon={Users} label="Customer Management" />
                        <Feature icon={TrendingUp} label="Sales Tracking" />
                        <Feature icon={PieChart} label="Real-time Analytics" />
                        <Feature icon={UsersRound} label="Team Collaboration" />
                    </div>
                </div>

                {/* Right sign-in panel */}
                <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
                    <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                        <div className="flex justify-center mb-4"><SaleHubMark className="w-14 h-14" /></div>
                        <h2 className="text-3xl font-bold text-slate-900 text-center tracking-tight">Welcome Back!</h2>
                        <p className="text-slate-500 text-center mt-1.5">Sign in to continue to Sale Hub</p>

                        <div className="mt-8 space-y-4">
                            <div>
                                <label className="text-sm font-semibold text-slate-700">Email Address</label>
                                <div className="relative mt-1.5">
                                    <Mail className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email" className={field} data-testid="login-email" autoComplete="username" />
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-slate-700">Password</label>
                                <div className="relative mt-1.5">
                                    <Lock className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password" className={`${field} pr-11`} data-testid="login-password" autoComplete="current-password" />
                                    <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                        {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end mt-3">
                            <button type="button" onClick={() => toast("Please contact your administrator to reset your password.")}
                                className="text-sm font-medium text-blue-600 hover:underline">Forgot Password?</button>
                        </div>

                        {error && <div className="mt-3 text-sm text-red-600 text-center" data-testid="login-error">{error}</div>}

                        <button type="submit" disabled={busy}
                            className="mt-4 w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold inline-flex items-center justify-center gap-2 transition disabled:opacity-60 shadow-sm shadow-blue-600/20"
                            data-testid="login-submit">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
                        </button>

                        <p className="text-center text-sm text-slate-500 mt-6">
                            Don&apos;t have an account? <span className="text-blue-600 font-semibold">Contact your administrator</span>
                        </p>
                    </form>
                </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure Login <span className="text-slate-300">|</span> © 2026 Sale Hub. All rights reserved.
            </div>
        </div>
    );
}
